import { yupResolver } from '@hookform/resolvers/yup';
import { Stack } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { FormProvider, useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { IField } from '../../../../data-models/field2.data-model';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
  customKpiTemplateSchema,
} from '../../../../schemas/kpi-template.schema';
import {
  selectedKpiConfigAtom,
  showKpiConfigFormAtom,
  useKpiConfigFormDefaults,
} from '../../kpi-config-form/kpi-config-form-state';
import { KpiConfigFormDialog } from '../../kpi-config-form/KpiConfigFormDialog';
import { KpiTemplateFormNavBlocker } from '../../KpiTemplateFormNavBlocker';
import { useCustomKpiTemplateDirty } from './hooks/useCustomKpiTemplateDirty';
import { matchesKpiSectionType } from './hooks/useFilteredKpiConfigs';
import { useSaveCustomKpiTemplate } from './hooks/useSaveCustomKpiTemplate';
import { AddSectionMenu } from './AddSectionMenu';
import { getDefaultFormMetaForSection } from './section-utils';
import { TemplateFormHeader } from './TemplateFormHeader';
import { TemplateSectionList } from './TemplateSectionList';

const ContentMaxWidth = '800px';

interface CustomKpiTemplateFormProps {
  initialValues: CustomKpiTemplateFormValues;
}

function useCustomKpiTemplateForm(initialValues: CustomKpiTemplateFormValues) {
  return useForm<CustomKpiTemplateFormValues>({
    defaultValues: initialValues,
    resolver: yupResolver(customKpiTemplateSchema().pick(['name', 'sections'])),
  });
}

function FormContent() {
  const { handleCreateNewKpi, handleKpiCreated, activeSection } = useKpiConfigDialogHandlers();
  const { control, trigger } = useFormContext<CustomKpiTemplateFormValues>();
  const { fields, append, remove, replace } = useFieldArray({ control, name: 'sections' });
  const { save } = useSaveCustomKpiTemplate();
  const isDirty = useCustomKpiTemplateDirty();
  const selectedKpiConfig = useAtomValue(selectedKpiConfigAtom);
  const formDefaults = useKpiConfigFormDefaults(
    activeSection
      ? {
          formMeta: getDefaultFormMetaForSection(activeSection.sectionType),
          ...(activeSection.kpiName ? { displayName: activeSection.kpiName } : {}),
        }
      : undefined
  );

  return (
    <Stack p='1rem' direction='column' height='100%' maxHeight='100%' width='100%' gap='1rem'>
      <Stack maxWidth={ContentMaxWidth} gap='1rem'>
        <TemplateFormHeader isDirty={isDirty} />
      </Stack>
      <Stack height='100%' overflow='auto'>
        <Stack maxWidth={ContentMaxWidth} gap='1rem'>
          <TemplateSectionList
            fields={fields}
            remove={remove}
            replace={replace}
            onCreateNewKpi={handleCreateNewKpi}
          />
          <AddSectionMenu append={append} />
        </Stack>
      </Stack>
      <KpiConfigFormDialog
        title={selectedKpiConfig ? 'Edit KPI' : 'Create KPI'}
        onSubmit={handleKpiCreated}
        defaultValues={formDefaults}
      />
      <KpiTemplateFormNavBlocker onSave={save} trigger={trigger} isDirty={isDirty} />
    </Stack>
  );
}

interface ActiveSectionContext {
  sectionIndex: number;
  sectionType: CustomTemplateSectionType;
  kpiName?: string;
}

function useKpiConfigDialogHandlers() {
  const setShowKpiConfigForm = useSetAtom(showKpiConfigFormAtom);
  const { watch, setValue } = useFormContext<CustomKpiTemplateFormValues>();
  const [activeSection, setActiveSection] = useState<ActiveSectionContext | null>(null);

  const handleCreateNewKpi = useCallback(
    (sectionIndex: number, sectionType: CustomTemplateSectionType, kpiName?: string) => {
      setActiveSection({ sectionIndex, sectionType, kpiName });
      setShowKpiConfigForm(true);
    },
    [setShowKpiConfigForm]
  );

  const handleKpiCreated = useCallback(
    (data: IField<unknown>) => {
      const context = activeSection;
      setActiveSection(null);
      if (!context) {
        return;
      }

      if (!matchesKpiSectionType(data, context.sectionType)) {
        return;
      }

      const sections = watch('sections');
      const section = sections[context.sectionIndex];
      if (!section) {
        return;
      }

      const currentKpis = section.kpis ?? [];
      if (currentKpis.includes(data.id)) {
        return;
      }

      setValue(`sections.${context.sectionIndex}.kpis`, [...currentKpis, data.id], { shouldDirty: true });
    },
    [activeSection, watch, setValue]
  );

  return { handleCreateNewKpi, handleKpiCreated, activeSection };
}

export function CustomKpiTemplateForm({ initialValues }: CustomKpiTemplateFormProps) {
  const methods = useCustomKpiTemplateForm(initialValues);

  return (
    <FormProvider {...methods}>
      <FormContent />
    </FormProvider>
  );
}
